import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { TransactionsService } from '../../../core/services/api-services/transactions.service';
import { AuthService } from '../../../core/services/client-services/auth.service';
import { BreadcrumbComponent, BreadcrumbItem } from '../../../shared/components/breadcrumb/breadcrumb.component';
import { BreadcrumbService } from '../../../shared/components/breadcrumb/service/breadcrumb.service';
import { PaginatorComponent } from "../../../shared/components/paginator/paginator.component";
import { ApprovePayrollPaymentRequest, PayrollCompany, PayrollPaymentDetail, PayrollPaymentSummary, PayrollStatus, RejectPayrollPaymentRequest } from './payroll-payment.model';

declare var bootstrap: any;

type PayrollTab = 'approved' | 'pending' | 'rejected';

/** statusId values that belong to each tab */
const TAB_STATUS: Record<PayrollTab, number[]> = {
  approved: [PayrollStatus.PAID, PayrollStatus.TO_BE_PAID],
  pending: [PayrollStatus.PENDING],
  rejected: [PayrollStatus.REJECTED],
};

@Component({
  selector: 'app-payroll-payments',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginatorComponent, BreadcrumbComponent],
  providers: [DatePipe],
  templateUrl: './payroll-payments.component.html',
  styleUrl: './payroll-payments.component.css'
})
export class PayrollPaymentsComponent implements OnInit, OnDestroy {
  breadcrumbItems: BreadcrumbItem[] = [];
  private destroy$ = new Subject<void>();
  private datasubscriber$ = new Subscription();

  // ── Tab ──────────────────────────────────────────────────────────
  activeTab: PayrollTab = 'approved';

  // ── Approved Companies ───────────────────────────────────────────
  approvedCompanies: PayrollCompany[] = [];
  filteredApprovedCompanies: PayrollCompany[] = [];
  pagedApprovedCompanies: PayrollCompany[] = [];
  approvedSearchText = '';
  isApprovedLoading = false;
  approvedError = '';
  expandedApprovedIds = new Set<number>();
  approvedPaymentsMap = new Map<number, PayrollPaymentSummary[]>();
  approvedPaymentsLoadingMap: Record<number, boolean> = {};
  approvedPaymentsErrorMap: Record<number, string> = {};

  // ── Pending Companies ────────────────────────────────────────────
  pendingCompanies: PayrollCompany[] = [];
  filteredPendingCompanies: PayrollCompany[] = [];
  pagedPendingCompanies: PayrollCompany[] = [];
  pendingSearchText = '';
  isPendingLoading = false;
  pendingError = '';
  expandedPendingIds = new Set<number>();
  pendingPaymentsMap = new Map<number, PayrollPaymentSummary[]>();
  pendingPaymentsLoadingMap: Record<number, boolean> = {};
  pendingPaymentsErrorMap: Record<number, string> = {};

  // ── Rejected Companies ───────────────────────────────────────────
  rejectedCompanies: PayrollCompany[] = [];
  filteredRejectedCompanies: PayrollCompany[] = [];
  pagedRejectedCompanies: PayrollCompany[] = [];
  rejectedSearchText = '';
  isRejectedLoading = false;
  rejectedError = '';
  expandedRejectedIds = new Set<number>();
  rejectedPaymentsMap = new Map<number, PayrollPaymentSummary[]>();
  rejectedPaymentsLoadingMap: Record<number, boolean> = {};
  rejectedPaymentsErrorMap: Record<number, string> = {};

  // ── Offcanvas / Detail ───────────────────────────────────────────
  selectedCompany: PayrollCompany | null = null;
  selectedPayment: PayrollPaymentSummary | null = null;
  paymentDetail: PayrollPaymentDetail | null = null;
  isDetailLoading = false;
  detailError = '';
  isApproving = false;
  isRejecting = false;

  // ── Reject confirm modal (no reason field) ───────────────────────
  rejectModalInstance: any = null;

  // ── Slip preview state ────────────────────────────────────────────
  slipObjectUrl: string | null = null;
  slipIsPdf = false;
  isSlipLoading = false;
  slipLoadError = '';

  constructor(
    private transactionService: TransactionsService,
    protected authService: AuthService,
    private breadcrumbService: BreadcrumbService,
    private toastrService: ToastrService,
    private datePipe: DatePipe,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.breadcrumbService.breadcrumbs$
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => (this.breadcrumbItems = items));

    this.breadcrumbService.setBreadcrumbs([
      { label: 'Dashboard', url: '/dashboard', icon: 'bi bi-house-door' },
      { label: 'Manage Payments', icon: 'bi bi-credit-card' },
      { label: 'Payroll Payments', icon: 'bi bi-cash-stack' },
    ]);

    const tabParam = this.route.snapshot.queryParamMap.get('tab');
    if (tabParam === 'pending' || tabParam === 'approved' || tabParam === 'rejected') {
      this.activeTab = tabParam;
    }

    this.loadTab('approved');
    this.loadTab('pending');
    this.loadTab('rejected');
  }

  // ── Tab management ───────────────────────────────────────────────
  setActiveTab(tab: PayrollTab): void {
    this.activeTab = tab;
    this.loadTab(tab);
  }

  private loadTab(tab: PayrollTab): void {
    switch (tab) {
      case 'approved': this.fetchCompanies('approved'); break;
      case 'pending': this.fetchCompanies('pending'); break;
      case 'rejected': this.fetchCompanies('rejected'); break;
    }
  }

  // ── Fetch companies (generic) ────────────────────────────────────
  private fetchCompanies(tab: PayrollTab): void {
    const statusId = TAB_STATUS[tab][0]; // primary status for the query

    // Reset state before loading
    this.setTabCompanies(tab, []);
    this.setTabLoading(tab, true);
    this.setTabError(tab, '');

    this.datasubscriber$.add(
      this.transactionService.getCompaniesWithPayrollInvoice(statusId)
        .pipe(finalize(() => this.setTabLoading(tab, false)))
        .subscribe({
          next: res => {
            if (!res?.success) {
              this.setTabError(tab, res?.message || 'Unable to load companies.');
              this.setTabCompanies(tab, []);
              return;
            }
            this.setTabCompanies(tab, res.data ?? []);
            this.applySearch(tab);
            // Pre-fetch payments for all companies so the tab badge shows total record count
            if (tab === 'pending') {
              (res.data ?? []).forEach(c => this.fetchPaymentsForCompany(tab, Number(c.companyId)));
            }
          },
          error: err => {
            this.setTabError(tab, this.errMsg(err, 'Unable to load companies.'));
            this.setTabCompanies(tab, []);
          },
        })
    );
  }

  // ── Load payments for a company row (lazy + cached) ──────────────
  toggleExpand(tab: PayrollTab, company: PayrollCompany): void {
    const id = Number(company.companyId);
    const expandedSet = this.getExpandedSet(tab);
    const map = this.getPaymentsMap(tab);

    if (expandedSet.has(id)) { expandedSet.delete(id); return; }
    expandedSet.add(id);
    if (!map.has(id)) this.fetchPaymentsForCompany(tab, id);
  }

  isExpanded(tab: PayrollTab, companyId: number): boolean {
    return this.getExpandedSet(tab).has(Number(companyId));
  }

  private fetchPaymentsForCompany(tab: PayrollTab, companyId: number): void {
    const loadingMap = this.getPaymentsLoadingMap(tab);
    const errorMap = this.getPaymentsErrorMap(tab);
    const map = this.getPaymentsMap(tab);
    const statusId = TAB_STATUS[tab][0];

    loadingMap[companyId] = true;
    errorMap[companyId] = '';

    this.datasubscriber$.add(
      this.transactionService.getPayrollPaymentSummary(companyId, statusId)
        .pipe(finalize(() => { loadingMap[companyId] = false; }))
        .subscribe({
          next: res => {
            if (!res?.success) {
              errorMap[companyId] = res?.message || 'Unable to load payments.';
              map.set(companyId, []);
              return;
            }
            // For the approved tab, include both PAID and TO_BE_PAID records
            const all = res.data ?? [];
            const filtered = tab === 'approved'
              ? all.filter(p => TAB_STATUS.approved.includes(p.statusId))
              : all;
            map.set(companyId, filtered);
          },
          error: err => {
            errorMap[companyId] = this.errMsg(err, 'Unable to load payments.');
            map.set(companyId, []);
          },
        })
    );
  }

  get pendingPaymentTotalCount(): number {
    let total = 0;
    for (const payments of this.pendingPaymentsMap.values()) {
      total += payments.length;
    }
    return total;
  }

  // ── Offcanvas: open detail ───────────────────────────────────────
  openDetail(company: PayrollCompany, payment: PayrollPaymentSummary): void {
    this.resetSlipState();
    this.selectedCompany = company;
    this.selectedPayment = payment;
    this.paymentDetail = null;
    this.detailError = '';

    const statusId = payment.statusId;
    this.isDetailLoading = true;

    this.datasubscriber$.add(
      this.transactionService.getPayrollPaymentDetail(
        Number(company.companyId),
        payment.payDayPayRollHdrId,
        statusId
      )
        .pipe(finalize(() => { this.isDetailLoading = false; }))
        .subscribe({
          next: res => {
            if (!res?.success || !res.data?.length) {
              this.detailError = res?.message || 'Payment details not found.';
              return;
            }
            this.paymentDetail = res.data[0];
          },
          error: err => {
            this.detailError = this.errMsg(err, 'Unable to load payment details.');
          },
        })
    );
  }

  resetSlipState(): void {
    if (this.slipObjectUrl) {
      URL.revokeObjectURL(this.slipObjectUrl);
      this.slipObjectUrl = null;
    }
    this.slipIsPdf = false;
    this.isSlipLoading = false;
    this.slipLoadError = '';
  }

  // ── Load slip (fetch blob → object URL → preview + auto-download) ─
  loadPaymentSlip(
    paySlipUrl: string,
    companyId?: number | string,
    invoiceNumber?: string,
    slipVersion?: number | string,
    createdDate?: string
  ): void {
    if (!paySlipUrl) return;

    this.isSlipLoading = true;
    this.slipLoadError = '';

    this.datasubscriber$.add(
      this.transactionService.downlaodPayrollPaymentPayslip(paySlipUrl)
        .pipe(finalize(() => { this.isSlipLoading = false; }))
        .subscribe({
          next: (blob: Blob) => {
            // ── Detect type from actual MIME ───────────────────────
            this.slipIsPdf = blob.type === 'application/pdf' || blob.type.includes('pdf');
            const ext = this.slipIsPdf ? 'pdf' : this.mimeToExt(blob.type);

            // ── Build safe object URL for preview ─────────────────
            if (this.slipObjectUrl) URL.revokeObjectURL(this.slipObjectUrl);
            this.slipObjectUrl = URL.createObjectURL(blob);

            // ── Auto-download with proper filename ─────────────────
            const dateStr = this.datePipe.transform(createdDate, 'yyyy-MM-dd') ?? 'unknown';
            const fileName = `payroll-slip-${companyId}-${invoiceNumber}-v${slipVersion}-${dateStr}.${ext}`;
            this.triggerDownload(this.slipObjectUrl, fileName);
          },
          error: err => {
            this.slipLoadError = this.errMsg(err, 'Failed to load payment slip.');
            this.toastrService.error(this.slipLoadError, 'Error');
          },
        })
    );
  }

  // ── Open already-loaded slip in new tab ───────────────────────────
  openSlipInNewTab(): void {
    if (this.slipObjectUrl) window.open(this.slipObjectUrl, '_blank');
  }

  // ── Re-download already-loaded slip ───────────────────────────────
  reDownloadSlip(
    companyId?: number | string,
    invoiceNumber?: string,
    slipVersion?: number | string,
    createdDate?: string
  ): void {
    if (!this.slipObjectUrl) return;
    const ext = this.slipIsPdf ? 'pdf' : 'jpg';
    const dateStr = this.datePipe.transform(createdDate, 'yyyy-MM-dd') ?? 'unknown';
    const fileName = `payroll-slip-${companyId}-${invoiceNumber}-v${slipVersion}-${dateStr}.${ext}`;
    this.triggerDownload(this.slipObjectUrl, fileName);
  }

  private triggerDownload(objectUrl: string, fileName: string): void {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private mimeToExt(mime: string): string {
    const map: Record<string, string> = {
      'image/jpeg': 'jpg',
      'image/png': 'png',
      'image/webp': 'webp',
      'image/gif': 'gif',
    };
    return map[mime] ?? 'jpg';
  }

  // ── Approve ──────────────────────────────────────────────────────
  approvePayment(): void {
    if (!this.selectedCompany || !this.selectedPayment) return;

    const body: ApprovePayrollPaymentRequest = {
      companyId: Number(this.selectedCompany.companyId),
      payDayPayRollHdrId: this.selectedPayment.payDayPayRollHdrId,
      approvedBy: this.currentUserId(),
    };

    this.isApproving = true;
    this.detailError = '';

    this.datasubscriber$.add(
      this.transactionService.approvePayrollPayment(body)
        .pipe(finalize(() => { this.isApproving = false; }))
        .subscribe({
          next: res => {
            if (!res?.success) {
              this.detailError = res?.message || 'Failed to approve payment.';
              this.toastrService.error(this.detailError, 'Error');
              return;
            }
            this.toastrService.success(res?.message || 'Payment approved successfully.', 'Success');
            this.closeOffcanvas();
            this.loadTab('pending');
            this.loadTab('approved');
          },
          error: err => {
            this.detailError = this.errMsg(err, 'Failed to approve payment.');
            this.toastrService.error(this.detailError, 'Error');
          },
        })
    );
  }

  // ── Reject (no reason required) ──────────────────────────────────
  openRejectModal(): void {
    const el = document.getElementById('payrollRejectModal');
    if (!el) return;
    if (!this.rejectModalInstance) {
      this.rejectModalInstance = new bootstrap.Modal(el, { backdrop: 'static', keyboard: false });
    }
    this.rejectModalInstance.show();
  }

  closeRejectModal(): void {
    this.rejectModalInstance?.hide();
  }

  confirmReject(): void {
    if (!this.selectedCompany || !this.selectedPayment) return;

    const body: RejectPayrollPaymentRequest = {
      companyId: Number(this.selectedCompany.companyId),
      payDayPayRollHdrId: this.selectedPayment.payDayPayRollHdrId,
      rejectedBy: this.currentUserId(),
    };

    this.isRejecting = true;

    this.datasubscriber$.add(
      this.transactionService.rejectPayrollPayment(body)
        .pipe(finalize(() => { this.isRejecting = false; }))
        .subscribe({
          next: res => {
            if (!res?.success) {
              this.toastrService.error(res?.message || 'Failed to reject payment.', 'Error');
              return;
            }
            this.toastrService.success(res?.message || 'Payment rejected successfully.', 'Success');
            this.closeRejectModal();
            this.closeOffcanvas();
            this.loadTab('pending');
            this.loadTab('rejected');
          },
          error: err => {
            this.toastrService.error(this.errMsg(err, 'Failed to reject payment.'), 'Error');
          },
        })
    );
  }

  // ── Search ───────────────────────────────────────────────────────
  applySearch(tab: PayrollTab): void {
    const src = this.getAllCompanies(tab);
    const q = this.getSearchText(tab).trim().toLowerCase();
    const result = q
      ? src.filter(c => c.companyName.toLowerCase().includes(q) || String(c.companyId).includes(q))
      : [...src];
    this.setFilteredCompanies(tab, result);
  }

  onPageChange(tab: PayrollTab, paged: any[]): void {
    switch (tab) {
      case 'approved': this.pagedApprovedCompanies = paged; break;
      case 'pending': this.pagedPendingCompanies = paged; break;
      case 'rejected': this.pagedRejectedCompanies = paged; break;
    }
  }

  // ── Helpers ──────────────────────────────────────────────────────
  private currentUserId(): number {
    const u = this.authService.getUser();
    return Number(u?.portalAdminId) || 0;
  }

  private closeOffcanvas(): void {
    const el = document.getElementById('payrollDetailsOffcanvas');
    if (el) (window as any).bootstrap?.Offcanvas?.getInstance(el)?.hide();
  }

  private errMsg(err: any, fallback: string): string {
    return err?.error?.message || err?.error?.Message || err?.message || fallback;
  }

  // ── Tab state accessors ──────────────────────────────────────────
  private getExpandedSet(tab: PayrollTab): Set<number> {
    switch (tab) {
      case 'approved': return this.expandedApprovedIds;
      case 'pending': return this.expandedPendingIds;
      case 'rejected': return this.expandedRejectedIds;
    }
  }

  private getPaymentsMap(tab: PayrollTab): Map<number, PayrollPaymentSummary[]> {
    switch (tab) {
      case 'approved': return this.approvedPaymentsMap;
      case 'pending': return this.pendingPaymentsMap;
      case 'rejected': return this.rejectedPaymentsMap;
    }
  }

  private getPaymentsLoadingMap(tab: PayrollTab): Record<number, boolean> {
    switch (tab) {
      case 'approved': return this.approvedPaymentsLoadingMap;
      case 'pending': return this.pendingPaymentsLoadingMap;
      case 'rejected': return this.rejectedPaymentsLoadingMap;
    }
  }

  private getPaymentsErrorMap(tab: PayrollTab): Record<number, string> {
    switch (tab) {
      case 'approved': return this.approvedPaymentsErrorMap;
      case 'pending': return this.pendingPaymentsErrorMap;
      case 'rejected': return this.rejectedPaymentsErrorMap;
    }
  }

  private getAllCompanies(tab: PayrollTab): PayrollCompany[] {
    switch (tab) {
      case 'approved': return this.approvedCompanies;
      case 'pending': return this.pendingCompanies;
      case 'rejected': return this.rejectedCompanies;
    }
  }

  private setFilteredCompanies(tab: PayrollTab, data: PayrollCompany[]): void {
    switch (tab) {
      case 'approved': this.filteredApprovedCompanies = data; break;
      case 'pending': this.filteredPendingCompanies = data; break;
      case 'rejected': this.filteredRejectedCompanies = data; break;
    }
  }

  private setTabCompanies(tab: PayrollTab, data: PayrollCompany[]): void {
    switch (tab) {
      case 'approved': this.approvedCompanies = data; this.expandedApprovedIds.clear(); this.approvedPaymentsMap.clear(); break;
      case 'pending': this.pendingCompanies = data; this.expandedPendingIds.clear(); this.pendingPaymentsMap.clear(); break;
      case 'rejected': this.rejectedCompanies = data; this.expandedRejectedIds.clear(); this.rejectedPaymentsMap.clear(); break;
    }
  }

  private setTabLoading(tab: PayrollTab, v: boolean): void {
    switch (tab) {
      case 'approved': this.isApprovedLoading = v; break;
      case 'pending': this.isPendingLoading = v; break;
      case 'rejected': this.isRejectedLoading = v; break;
    }
  }

  private setTabError(tab: PayrollTab, msg: string): void {
    switch (tab) {
      case 'approved': this.approvedError = msg; break;
      case 'pending': this.pendingError = msg; break;
      case 'rejected': this.rejectedError = msg; break;
    }
  }

  getSearchText(tab: PayrollTab): string {
    switch (tab) {
      case 'approved': return this.approvedSearchText;
      case 'pending': return this.pendingSearchText;
      case 'rejected': return this.rejectedSearchText;
    }
  }

  getPaymentsFor(tab: PayrollTab, companyId: number): PayrollPaymentSummary[] {
    return this.getPaymentsMap(tab).get(Number(companyId)) ?? [];
  }

  isPaymentsLoading(tab: PayrollTab, companyId: number): boolean {
    return !!this.getPaymentsLoadingMap(tab)[Number(companyId)];
  }

  paymentsError(tab: PayrollTab, companyId: number): string {
    return this.getPaymentsErrorMap(tab)[Number(companyId)] ?? '';
  }

  ngOnDestroy(): void {
    if (this.slipObjectUrl) URL.revokeObjectURL(this.slipObjectUrl);
    this.datasubscriber$.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
